/**
 * RD-NAV-002 (Phase 2 — sub-tabs in the URL) — the single registry of every
 * sub-view level under `/app/<feature>[/<view>]` and `/platform/<feature>[/<view>]`.
 *
 * A "level" is one visible tab-bar row (e.g. the pool-lifecycle pills on the
 * route board, the Cobrar/All/Credits segments on invoices). Each level has a
 * `defaultKey` (the view with NO url segment) and a list of `views`, each
 * `{ key, slug, roles?, poolOnly?, nonPoolOnly? }`:
 *   - `key`   — the INTERNAL state value the consuming component already uses
 *               (unchanged by this work — see AGENTS.md "URLs are ALWAYS
 *               English…; internal state keys do NOT change").
 *   - `slug`  — the URL segment (English, lowercase, hyphenated). Founder
 *               decision (2026-09-23): a level's default view gets an
 *               EXPLICIT slug when its views are PEERS (tab-bar pills with no
 *               "this one is the real one" hierarchy) — e.g. `app/invoices`
 *               cobrar -> `collect`, sitting next to `all`/`credits`.
 *               '' (bare, no segment) is reserved for the two levels whose
 *               default view IS the unqualified whole — `app/customers` "all"
 *               and `platform/tenants` "overview" — never add a THIRD bare
 *               level without a matching founder decision. (Single-view
 *               levels — `app/jobs`'s one list, `app/invoices/credits` — have
 *               no peers, so their only view is bare too.)
 *   - `roles` — overrides the level's own `roles` for THIS view only (e.g. the
 *               customers "archived" segment is admin/operations-only —
 *               billing's CustomersSection has no Archived tab).
 *   - `poolOnly` / `nonPoolOnly` — gates on `ctx.isPool` (pool-industry tenant).
 *
 * Consumers never read SUB_VIEW_REGISTRY directly — use the pure helpers below
 * (`slugToKey`, `keyToSlug`, `isViewAllowed`) or the `useUrlSubView` hook.
 * Everything here fails CLOSED: an unknown slug, a role- or pool-disallowed
 * view, or a garbage key always resolves to the level's `defaultKey`.
 */

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const SUB_VIEW_REGISTRY = {
  // Route board (admin / supervisor / worker / operations). The pool-lifecycle
  // states are TWO-segment slugs (`season/<state>`) so a pool tenant's URL
  // reads as a sentence ("route, season, opening"); on-demand is available to
  // BOTH pool and non-pool tenants under a DIFFERENT slug per ctx.isPool —
  // handled as a special case in slugToKey/keyToSlug below (kept in THIS
  // module per the spec, not spread across call sites).
  'app/route': {
    defaultKey: 'stops',
    roles: ['admin', 'supervisor', 'worker', 'operations'],
    views: [
      { key: 'stops', slug: 'stops' },
      { key: 'onDemand', slug: 'on-demand' },
      { key: 'opening', slug: 'season/opening', poolOnly: true },
      { key: 'closing', slug: 'season/closing', poolOnly: true },
      { key: 'closed', slug: 'season/closed', poolOnly: true },
      { key: 'activity', slug: 'activity' },
    ],
  },
  // Nested UNDER app/route/activity — the logged/no-service/reschedules
  // feed tabs (ActivityTab). A separate level because it has its own
  // independent default (bare `/app/route/activity` = logged).
  'app/route/activity': {
    defaultKey: 'logged',
    roles: ['admin', 'supervisor', 'worker', 'operations'],
    views: [
      { key: 'logged', slug: 'logged' },
      { key: 'noService', slug: 'no-service' },
      { key: 'shifts', slug: 'reschedules' },
    ],
  },
  // RD-NAV-012 (KAN-693): Jobs is ONE list — no sub-views. The level stays
  // registered with its single bare view so `/app/jobs/<anything>` canonicalizes
  // to `/app/jobs` and the old Current / History / Reschedules URLs (redirected
  // in legacyUrlRedirects.js to `?group=` / `?status=`) can never strand a user.
  // The old `adminActiveJobTab` stored value (current | history | shifts) is no
  // longer read: every stored value now means "the list".
  'app/jobs': {
    defaultKey: 'current',
    roles: ['admin', 'billing', 'operations'],
    views: [
      { key: 'current', slug: '' },
    ],
  },
  'app/customers': {
    defaultKey: 'all',
    roles: ['admin', 'billing', 'operations'],
    views: [
      { key: 'all', slug: '' },
      // Verified against the section components: admin/operations'
      // CustomersSection renders all/customer/one_off/archived; billing's
      // BillingCustomersSection has NO "archived" segment (all/customer/one_off).
      { key: 'customer', slug: 'regular' },
      { key: 'one_off', slug: 'one-off' },
      { key: 'archived', slug: 'archived', roles: ['admin', 'operations'] },
    ],
  },
  'app/invoices': {
    defaultKey: 'cobrar',
    roles: ['admin', 'billing'],
    persist: {
      storageKey: (ctx) => (ctx?.role ? `crewvex.billing.segment.${ctx.role}` : null),
      migrate: (raw) => {
        const LEGACY_SEGMENT_MIGRATIONS = { ready: 'cobrar', drafts: 'cobrar' };
        if (Object.prototype.hasOwnProperty.call(LEGACY_SEGMENT_MIGRATIONS, raw)) {
          return LEGACY_SEGMENT_MIGRATIONS[raw];
        }
        return raw;
      },
    },
    views: [
      // Internal key stays 'cobrar' (untouched); the URL slug follows the
      // English UI label ("Collect"), per the founder's explicit-default rule.
      { key: 'cobrar', slug: 'collect' },
      { key: 'all', slug: 'all' },
      { key: 'credits', slug: 'credits' },
    ],
  },
  // Child level of app/invoices -> credits. Registered so useUrlSubView
  // ('app/invoices') treats `credits/<customerId>` as owned by this level
  // (ownedByChild) instead of stripping the deeper segment. The only view is
  // the bare list ("everything", allowed bare by RD-NAV-002); the per-customer
  // detail segment is a UUID validated by parseCreditDetailCustomerId, never a
  // registered slug (an id is data, not a tab).
  'app/invoices/credits': {
    defaultKey: 'list',
    roles: ['admin', 'billing'],
    views: [{ key: 'list', slug: '' }],
  },
  'app/estimates': {
    defaultKey: 'active',
    roles: ['admin', 'billing'],
    persist: {
      storageKey: () => 'crewvex.estimateListScope',
      migrate: (raw) => (raw === 'archived' ? 'archived' : raw === 'active' ? 'active' : null),
    },
    views: [
      { key: 'active', slug: 'active' },
      { key: 'archived', slug: 'archived' },
    ],
  },
  'app/catalog': {
    defaultKey: 'services',
    roles: ['admin'],
    views: [
      { key: 'services', slug: 'services' },
      { key: 'chemicals', slug: 'chemicals' },
      { key: 'overrides', slug: 'overrides' },
    ],
  },
  'app/reports': {
    defaultKey: 'monthly',
    roles: ['admin', 'billing'],
    views: [
      { key: 'monthly', slug: 'monthly' },
      { key: 'yearly', slug: 'yearly' },
    ],
  },
  'app/history': {
    defaultKey: 'activity',
    roles: ['admin'],
    views: [
      { key: 'activity', slug: 'activity' },
      { key: 'communications', slug: 'communications' },
    ],
  },
  // KAN-728: Company = Settings | Plan & billing. ADMIN only (owner + admins;
  // billing/operations/supervisor/worker fail closed to the default, and the
  // plan RPC is admin-only server-side too). A `plan` slug for any other role
  // resolves to `settings`.
  'app/company': {
    defaultKey: 'settings',
    roles: ['admin'],
    views: [
      { key: 'settings', slug: 'settings' },
      { key: 'plan', slug: 'plan' },
    ],
  },
  'app/account': {
    defaultKey: 'settings',
    roles: ['admin', 'supervisor', 'worker', 'billing', 'operations'],
    views: [
      { key: 'settings', slug: 'settings' },
      { key: 'activity', slug: 'activity' },
    ],
  },
  // Platform (single-role surface — no `roles` gate needed).
  'platform/invites': {
    defaultKey: 'activity',
    views: [
      { key: 'activity', slug: 'activity' },
      { key: 'open', slug: 'open' },
    ],
  },
  // Platform's Account tab (settings/activity) — the same shape as the four
  // tenant shells' 'app/account' level, added 2026-09-23 so platform stops
  // carrying its own bespoke `?acct_tab=` query-param mechanism (which was a
  // SECOND source of truth that could disagree with the URL — see
  // AccountTab.js). Single-role surface, no `roles` gate, matching
  // 'platform/invites' above.
  'platform/account': {
    defaultKey: 'settings',
    views: [
      { key: 'settings', slug: 'settings' },
      { key: 'activity', slug: 'activity' },
    ],
  },
  // Tenant detail. Reached at a DYNAMIC base path (`platform/tenants/:id`) —
  // callers pass `{ basePath: 'platform/tenants/<id>' }` to useUrlSubView
  // rather than relying on the literal 'platform/tenants' prefix.
  'platform/tenants': {
    defaultKey: 'overview',
    views: [
      { key: 'overview', slug: '' },
      { key: 'members', slug: 'members' },
      { key: 'plan', slug: 'plan' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function normalizeSlug(raw) {
  return String(raw || '').replace(/^\/+/, '').replace(/\/+$/, '');
}

function findView(levelId, key) {
  const level = SUB_VIEW_REGISTRY[levelId];
  if (!level || !key) return null;
  return level.views.find((v) => v.key === key) || null;
}

/** Can `ctx.role` (+ `ctx.isPool`) see this view? Fails closed on any unknown input. */
export function isViewAllowed(levelId, key, ctx = {}) {
  const level = SUB_VIEW_REGISTRY[levelId];
  if (!level) return false;
  const view = findView(levelId, key);
  if (!view) return false;
  const roles = view.roles || level.roles;
  if (roles && !roles.includes(ctx.role)) return false;
  if (view.poolOnly && !ctx.isPool) return false;
  if (view.nonPoolOnly && ctx.isPool) return false;
  return true;
}

// Route's on-demand view is reachable under TWO slugs depending on whether
// the tenant is pool-industry — see the spec's "Pool season" note. Every
// other view has one fixed slug (gated by poolOnly/nonPoolOnly instead).
function routeOnDemandSlug(ctx) {
  return ctx?.isPool ? 'season/on-demand' : 'on-demand';
}

/**
 * URL sub-view segment (already stripped of the level's own base path) -> the
 * internal key. Always returns a valid, ALLOWED key for `ctx` — an unknown
 * slug, a role-disallowed view, or a pool-mismatched season/* slug resolves
 * to the level's defaultKey (never null, never throws).
 */
export function slugToKey(levelId, rawSlug, ctx = {}) {
  const level = SUB_VIEW_REGISTRY[levelId];
  if (!level) return null;
  const remaining = normalizeSlug(rawSlug);

  if (levelId === 'app/route' && (remaining === 'on-demand' || remaining === 'season/on-demand')) {
    return isViewAllowed(levelId, 'onDemand', ctx) ? 'onDemand' : level.defaultKey;
  }

  if (remaining === '') {
    const def = level.views.find((v) => v.slug === '');
    if (def && isViewAllowed(levelId, def.key, ctx)) return def.key;
    return level.defaultKey;
  }

  // Longest-slug-first: a 2-segment slug (season/opening) must not be
  // shadowed by an unrelated 1-segment prefix collision.
  const candidates = level.views
    .filter((v) => v.slug !== '')
    .slice()
    .sort((a, b) => b.slug.length - a.slug.length);
  for (const view of candidates) {
    if (remaining === view.slug || remaining.startsWith(`${view.slug}/`)) {
      return isViewAllowed(levelId, view.key, ctx) ? view.key : level.defaultKey;
    }
  }
  return level.defaultKey;
}

/**
 * Internal key -> the CANONICAL url segment for `ctx` (never the '/'-prefixed
 * form — callers join it onto the level's base path). A disallowed key
 * resolves to the level's default view's slug (fail closed).
 */
export function keyToSlug(levelId, key, ctx = {}) {
  const level = SUB_VIEW_REGISTRY[levelId];
  if (!level) return '';
  const resolvedKey = isViewAllowed(levelId, key, ctx) ? key : level.defaultKey;
  if (levelId === 'app/route' && resolvedKey === 'onDemand') {
    return routeOnDemandSlug(ctx);
  }
  const view = findView(levelId, resolvedKey);
  return view ? view.slug : '';
}

// ---------------------------------------------------------------------------
// Remembered-view map (module-level, in-memory; cleared on sign-out)
// ---------------------------------------------------------------------------

const rememberedViews = new Map();

/** Record the last sub-view a level was on — read back by a hidden tab (useUrlSubView) or a cross-feature nav (useFeatureTabRouting/usePlatformFeatureRouting composing a target path). */
export function rememberView(levelId, key) {
  if (!levelId || !key) return;
  rememberedViews.set(levelId, key);
}

export function getRememberedView(levelId) {
  return rememberedViews.get(levelId) ?? null;
}

/** Sign-out hook — wired next to clearLastRoute() in AuthContext.js. */
export function clearRememberedViews() {
  rememberedViews.clear();
}

// ---------------------------------------------------------------------------
// Optional localStorage seeding — the 3 levels that persisted a sub-tab
// BEFORE this work (admin jobs, invoices segment, estimates scope). A level
// with no `persist` entry (or whose storageKey resolves to null for this ctx)
// is a no-op here.
// ---------------------------------------------------------------------------

function persistConfigFor(levelId, ctx) {
  const cfg = SUB_VIEW_REGISTRY[levelId]?.persist;
  if (!cfg) return null;
  const storageKey = typeof cfg.storageKey === 'function' ? cfg.storageKey(ctx) : cfg.storageKey;
  if (!storageKey) return null;
  return { storageKey, migrate: cfg.migrate };
}

/** Read + validate the level's persisted default for `ctx`, or null. */
export function seedFromStorage(levelId, ctx = {}) {
  const cfg = persistConfigFor(levelId, ctx);
  if (!cfg) return null;
  try {
    const raw = localStorage.getItem(cfg.storageKey);
    if (!raw) return null;
    const key = cfg.migrate ? cfg.migrate(raw) : raw;
    if (key && isViewAllowed(levelId, key, ctx)) return key;
    return null;
  } catch (_) {
    return null;
  }
}

/** Write-through for a level's persisted key (keeps the legacy storage key live). */
export function persistView(levelId, ctx, key) {
  const cfg = persistConfigFor(levelId, ctx);
  if (!cfg || !key) return;
  try {
    localStorage.setItem(cfg.storageKey, key);
  } catch (_) {
    /* ignore quota / private mode */
  }
}

// ---------------------------------------------------------------------------
// Composed path (level + any registered CHILD level), shared by every
// caller that needs to navigate to a level's FULLY canonical path in one
// shot — never a bare intermediate relying on a later canonicalizing
// redirect. Used by useFeatureTabRouting.setActiveTab,
// usePlatformFeatureRouting.setActiveFeature, and useUrlSubView.setKey.
// ---------------------------------------------------------------------------

/**
 * The URL suffix (no leading '/', not prefixed with the level's own base
 * path) for `levelId` at `key`, INCLUDING a registered CHILD level's own
 * remembered/seeded/default view when one exists — e.g. `app/route` at key
 * `activity` composes `activity/logged` (the child level `app/route/activity`'s
 * own current view), not just `activity`. A disallowed/unknown key resolves
 * to the level's default (keyToSlug already fails closed); an unregistered
 * levelId returns ''.
 */
export function composeLevelPath(levelId, key, ctx = {}) {
  const level = SUB_VIEW_REGISTRY[levelId];
  if (!level) return '';
  const resolvedKey = isViewAllowed(levelId, key, ctx) ? key : level.defaultKey;
  const slug = keyToSlug(levelId, resolvedKey, ctx);
  let suffix = slug || '';

  const childLevelId = resolvedKey ? `${levelId}/${resolvedKey}` : null;
  const childLevel = childLevelId ? SUB_VIEW_REGISTRY[childLevelId] : null;
  if (childLevel) {
    const childKey = getRememberedView(childLevelId) ?? seedFromStorage(childLevelId, ctx) ?? childLevel.defaultKey;
    const childSlug = childKey ? keyToSlug(childLevelId, childKey, ctx) : '';
    if (childSlug) suffix = suffix ? `${suffix}/${childSlug}` : childSlug;
  }
  return suffix;
}

const CREDIT_LIST_PATH = '/app/invoices/credits';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CREDIT_ROLES = ['admin', 'billing'];

/**
 * `/app/invoices/credits/<customerId>` — the per-customer credit detail.
 * A non-UUID (or missing) id returns the list path: only a customer UUID may
 * ever appear in this URL (no names, no search text).
 */
export function creditDetailPath(customerId) {
  return typeof customerId === 'string' && UUID_RE.test(customerId)
    ? `${CREDIT_LIST_PATH}/${customerId}`
    : CREDIT_LIST_PATH;
}

/**
 * The customer id in a credit-detail pathname, or null. Fails closed: exact
 * `/app/invoices/credits/<uuid>` only (one optional trailing slash), and only
 * for admin/billing.
 */
export function parseCreditDetailCustomerId(pathname, ctx = {}) {
  if (!CREDIT_ROLES.includes(ctx?.role)) return null;
  if (typeof pathname !== 'string') return null;
  const prefix = `${CREDIT_LIST_PATH}/`;
  if (!pathname.startsWith(prefix)) return null;
  const id = pathname.slice(prefix.length).replace(/\/$/, '');
  return UUID_RE.test(id) ? id : null;
}
